import { useState, useCallback } from "react";
import { UseFormReturn } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { useCreateEntryConfig } from "@/services/dataService";
import { EntryConfigUnion } from "@/types/entryConfig";

interface EntryConfigData {
  entryConfigId: string;
  minNumSamples: number;
}

interface UseTraitSelectionProps {
  form: UseFormReturn<any>;
  selectedEntryConfigData: EntryConfigData[];
  /** Set of trait IDs that were added in the field (have userVisibility) */
  fieldAddedTraitIds?: Set<string>;
}

export function useTraitSelection({ form, selectedEntryConfigData, fieldAddedTraitIds }: UseTraitSelectionProps) {
  const [showAddTraitDialog, setShowAddTraitDialog] = useState(false);
  // Track field-added traits that were toggled off during this editing session
  // Used to determine if userVisibility should be cleared on save (promoted to global)
  const [toggledOffFieldTraitIds, setToggledOffFieldTraitIds] = useState<Set<string>>(new Set());
  const { toast } = useToast();
  const createEntryConfig = useCreateEntryConfig();

  const toggleEntryConfig = useCallback((entryConfigId: string) => {
    const currentEntryConfigData = form.getValues("entryConfigData") || [];
    const existingIndex = currentEntryConfigData.findIndex((d: EntryConfigData) => d.entryConfigId === entryConfigId);

    if (existingIndex !== -1) {
      // Toggling OFF - if this is a field-added trait, track it
      if (fieldAddedTraitIds?.has(entryConfigId)) {
        setToggledOffFieldTraitIds(prev => new Set(prev).add(entryConfigId));
      }
      form.setValue(
        "entryConfigData",
        currentEntryConfigData.filter((d: EntryConfigData) => d.entryConfigId !== entryConfigId),
        { shouldDirty: true }
      );
    } else {
      form.setValue("entryConfigData", [...currentEntryConfigData, {
        entryConfigId,
        minNumSamples: 1 // Default to 1 sample
      }], { shouldDirty: true });
    }
  }, [form, fieldAddedTraitIds]);

  const updateMinNumSamples = (entryConfigId: string, minNumSamples: number) => {
    const currentEntryConfigData = form.getValues("entryConfigData") || [];
    const updatedData = currentEntryConfigData.map((d: EntryConfigData) =>
      d.entryConfigId === entryConfigId ? { ...d, minNumSamples } : d
    );
    form.setValue("entryConfigData", updatedData, { shouldDirty: true });
  };

  const handleAddTrait = async (
    trait: Omit<EntryConfigUnion, "id" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy">
  ) => {
    try {
      const createdEntryConfig = await createEntryConfig(trait);

      if (createdEntryConfig) {
        // Start from the latest selected traits (avoids stale state) and select the new trait
        const currentEntryConfigData: EntryConfigData[] = form.getValues("entryConfigData") || [];
        const alreadySelected = currentEntryConfigData.some((d) => d.entryConfigId === createdEntryConfig.id);
        const updatedEntryConfigData = alreadySelected
          ? currentEntryConfigData
          : [
              ...currentEntryConfigData,
              {
                entryConfigId: createdEntryConfig.id!,
                minNumSamples: 1,
              },
            ];

        form.setValue("entryConfigData", updatedEntryConfigData, { shouldDirty: true });

        toast({
          title: "Trait added",
          description: `${createdEntryConfig.name} has been added to available traits`,
        });
      }
    } catch (error) {
      console.error("Error adding trait:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add trait",
        variant: "destructive",
      });
    }
  };

  return {
    showAddTraitDialog,
    setShowAddTraitDialog,
    toggleEntryConfig,
    updateMinNumSamples,
    handleAddTrait,
    /** Set of field-added trait IDs that were toggled off during this session */
    toggledOffFieldTraitIds,
  };
}
